import React, { Fragment } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { SkippedIcon } from "./icons/skippedIcon";
import { EditIcon } from "./icons/editIcon";

const POSSIBLE_ICONS = {
  skipped: 0,
  edit: 1
};

const ICONS_TO_COMPONENT = {
  [POSSIBLE_ICONS.skipped]: (SkippedIcon),
  [POSSIBLE_ICONS.edit]: (EditIcon)
}

const StepperContainer = styled.ol`
  display: flex;
  flex: 1 1 0%;
  -webkit-box-align: center;
  align-items: center;
  padding-left: 0px;
  overflow-x: auto;
  margin: 0px;
  list-style: none;

  ::-webkit-scrollbar {
    height: 12px;
    width: 12px
  }

  ::-webkit-scrollbar-track, ::-webkit-scrollbar-thumb {
    border: 4px solid transparent;
    background-clip: padding-box;
    border-radius: 16px
  }

  ::-webkit-scrollbar-track {
    background-color: var(--se-divider, #e6e6e6);
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--se-border, #9fa0a4);
  }
`;

const StepperItem = styled.li`
  flex: 1 1 0px;
  justify-content: center;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 14px 20px 12px;
  border-bottom: 5px solid #ededed;
  color: #626469;
  cursor: pointer;
  &.active {
    border-bottom-color: ${props => props.$color ? props.$color : '#008a16'};

    color: ${props => props.$color ? props.$color : '#008a16'};
    cursor: default;
    font-weight: bold;
  }
  &.disabled {
    color: #626469;
    cursor: default;
    opacity: 0.5;
  }
  @media (max-width: ${(props) => props.mobileBreakpoint}px) {
    flex-direction: column;
    border-bottom: none;
    padding: 14px 5px 12px;
  }
`;

const StepperItemLabel = styled.span`
  font-size: 15px;
  line-height: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-left: 5px;
  text-transform: uppercase;
  ${(props) =>
    props.$done || props.$isActive
      ? `
  font-weight: bold;
  `
      : ``}
  @media (max-width: ${(props) => props.mobileBreakpoint}px) {
    display: none;
  }
`;

const StepperItemDoneLabel = styled.span`
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-left: 5px;

  @media (max-width: ${(props) => props.mobileBreakpoint}px) {
    font-size: 10px;
    margin-left: 0px;
  }
`;

const StepperIconBeforeItem = styled.span`
  position: relative;
  box-sizing: content-box;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  border: 2px solid #626469;
  background-size: 10px;
  background-repeat: no-repeat;
  background-position: center center;

  ${(props) =>
    props.$done
      ? `
background-color: ${props.$color ? props.$color : '#008a16'};
border-color: ${props.$color ? props.$color : '#008a16'};
color: ${props.$textColor ? props.$textColor : 'white'};
`
      : ``}
  ${(props) =>
    props.$isActive && !props.$done
      ? `
  border-color: ${props.$color ? props.$color : '#008a16'};
`
      : ``}
`;

const StepperGlobalItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const MsxIconsSvg = styled.span`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    display: block;
    width: 14px;
    height: 14px;
    min-width: 14px;
    min-height: 14px;
    max-width: 14px;
    max-height: 14px;
  }
`;

const EmptyCircle = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: white;
  border-radius: 50%;
`;

const CircleMask = styled.div`
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background-color: black;
  opacity: 0.2;
  border-radius: 50%;
  z-index: 0;
`;

const MobileStepperItemLabel = styled.span`
  display: none;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  text-transform: uppercase;
  ${(props) =>
    props.$done || props.$isActive
      ? `
  font-weight: bold;
  `
      : ``}
  @media (max-width: ${(props) => props.mobileBreakpoint}px) {
    display: block;
    font-size: 12px;
  }
  @media (max-width: ${(props) => props.nanoMobileBreakpoint}px) {
    max-width: ${(props) => props.nanoMobileMaxWidth}px;
    font-size: 10px;
    text-overflow: ellipsis;
  }
`;

const MobileBar = styled.span`
  @media (max-width: ${(props) => props.mobileBreakpoint}px) {
    display: flex;
    flex: 1 2 0px;
    height: 0.5px;
    background-color: #626469;
  }
`;

const standardize_color = (str) => {
  var ctx = document.createElement('canvas').getContext('2d');
  ctx.fillStyle = str;
  return ctx.fillStyle.length > 0 && ctx.fillStyle[0] === '#' ? ctx.fillStyle : rgb2hex(ctx.fillStyle);
}

const rgb2hex = (rgb) => {
  rgb = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
  return (rgb && rgb.length === 4) ? "#" +
    ("0" + parseInt(rgb[1], 10).toString(16)).slice(-2) +
    ("0" + parseInt(rgb[2], 10).toString(16)).slice(-2) +
    ("0" + parseInt(rgb[3], 10).toString(16)).slice(-2) : '';
}

const hexToRgb = hex =>
  hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
    , (m, r, g, b) => '#' + r + r + g + g + b + b)
    .substring(1).match(/.{2}/g)
    .map(x => parseInt(x, 16));


const rgb_is_light = (color) => {
  const brightness = ((color[0] * 299) + (color[1] * 587) + (color[2] * 114)) / 1000;
  return brightness > 155;
}

class Stepper extends React.Component {
  static propTypes = {
    steps: PropTypes.array,
    mobileBreakpoint: PropTypes.number,
    disabled: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      textColor: rgb_is_light(hexToRgb(standardize_color(props.color || `#008a16`))) ? 'rgb(98,100,105)' : 'white',
    }
  }

  componentDidMount() {
    if (this._stepperRef && this._activeItemRef) {
      try {
        this._stepperRef.current.scrollLeft =
          this._activeItemRef.current.offsetLeft;
      } catch (error) {}
    }
  }

  computeStepID = (stepName, disabled, isActivable, isMobile) => {
    let stepId = stepName + "-stepper";
    if (isMobile) {
      stepId += "-mobile";
    }
    if (disabled || !isActivable) {
      stepId += "-disabled";
    }
    return stepId;
  };


  render() {
    const { steps } = this.props;
    const orderedSteps = steps.sort((a, b) => a.order - b.order);
    const lastItem = orderedSteps.length
      ? orderedSteps[orderedSteps.length - 1]
      : null;
    return (
      <StepperContainer>
        {steps.map((step) => {
          const isActive = step.isActive;
          const isDone = step.done;
          const isSkipped = step.skipped;
          const isLast = lastItem.id === step.id;
          const isActivable = step.active;
          const isHidden = step.hidden || false;
          return isHidden ? (
            <Fragment key={step.id}></Fragment>
          ) : (
            <Fragment key={step.id}>
              <StepperItem
                key={step.id}
                id={this.computeStepID(
                  step.name,
                  this.props.disabled,
                  isActivable,
                  false
                )}
                onClick={(event) => {
                  if (!this.props.disabled && isActivable) {
                    step.action();
                  }
                }}
                className={[
                  isActive ? "active" : isActivable ? "" : "disabled",
                ]
                  .filter((s) => s.length)
                  .join(" ")}
                mobileBreakpoint={this.props.mobileBreakpoint}
                $color={this.props.color}
              >
                <MobileStepperItemLabel
                  key={step.id}
                  $done={isDone || isSkipped}
                  mobileBreakpoint={this.props.mobileBreakpoint}
                  nanoMobileBreakpoint={this.props.nanoMobileBreakpoint != null ? this.props.nanoMobileBreakpoint : 650}
                  nanoMobileMaxWidth={this.props.nanoMobileMaxWidth != null ? this.props.nanoMobileMaxWidth : 60}
                >
                  {step.name}
                </MobileStepperItemLabel>
                <StepperIconBeforeItem
                  $done={isDone || isSkipped}
                  $isActive={isActive}
                  $color={this.props.color}
                  $textColor={this.state.textColor}
                >
                  <CircleMask></CircleMask>
                  {!isDone && !isSkipped && <EmptyCircle></EmptyCircle>}
                  {(isDone || isSkipped) && (
                    <MsxIconsSvg>
                      {ICONS_TO_COMPONENT[isSkipped ? POSSIBLE_ICONS.skipped : POSSIBLE_ICONS.edit] || <></>}
                    </MsxIconsSvg>
                  )}
                </StepperIconBeforeItem>
                <StepperGlobalItem>
                  <StepperItemLabel
                    mobileBreakpoint={this.props.mobileBreakpoint}
                    $done={isDone || isSkipped}
                    $isActive={isActive}
                  >
                    {step.name}
                  </StepperItemLabel>
                  <StepperItemDoneLabel
                    mobileBreakpoint={this.props.mobileBreakpoint}
                  >
                    {step.subLabel}
                  </StepperItemDoneLabel>
                </StepperGlobalItem>
              </StepperItem>
              {!isLast && (
                <MobileBar mobileBreakpoint={this.props.mobileBreakpoint} />
              )}
            </Fragment>
          );
        })}
      </StepperContainer>
    );
  }
}

export default Stepper;
